require("./threepl-service-view.scss");

import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal} from "@angular/core";
import {BreakpointObserver} from "@angular/cdk/layout";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {TranslateService} from "@ngx-translate/core";
import {map} from "rxjs";
import * as _ from "lodash";
import {BzTab} from "@bringoz/ui";
import {
  bzIconAccount, bzIconAgent, bzIconBranch, bzIconCarrier, bzIconDollarCircle, bzIconEdit, bzIconHash, bzIconInfoOutline,
  bzIconLabelOutline, bzIconMapOutline, bzIconPackageOutline, bzIconPhoneOutline, bzIconPrivatecar, bzIconProgram, bzIconTagOutline,
  bzIconTruckOutline, bzIconVolume, bzIconWeight,
} from "@bringoz/ui/icons";
import {ActionReasonEntityEnum} from "../../../../action-reasons/enums/action-reason-entity.enum";
import {ThreePLServiceDetails} from "../../models/threepl-service-details.model";
import {ThreePLServiceHttpService} from "../../services/threepl-service-http.service";
import {ThreePLServiceActionsService} from "../../services/threepl-service-actions.service";
import {ThreePLServicesChannelService, applyServicePush} from "../../services/threepl-services-channel.service";
import {ServiceActionEvent} from "../threepl-service-actions/threepl-service-actions.component";
import {
  ActionReasonsDialogsServiceService,
} from "../../../../action-reasons/services/action-reasons-dialogs-service.service";
import {CommonsDialogService} from "../../../../commons/services/commons-dialog.service";
import {SplitService} from "../../../../commons/services/split-service.service";
import {FulfillmentDialogService} from "../../../../fulfillment/services/fulfillment-dialogs-service.service";
import {FulfillmentService} from "../../../../fulfillment/services/fulfillment-service.service";
import {MEDIA_GT_MD} from "../../../../commons/angular/constants/media-breakpoints.constant";
import {SiteContextService} from "../../../../commons/services/site-context.service";
import {ILogService} from "angular";

interface InitialDerived {
  omsOrderIds: string[];
  programNames: string[];
  branchIds: string[][];
  dynamicColumnsMap: Record<string, {key: string; value?: unknown}>;
}

type ServiceTab = "info" | "timeline" | "map";

@Component({
  selector: "app-threepl-service-view",
  templateUrl: "./threepl-service-view.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeplServiceViewComponent implements OnInit {
  readonly serviceId = input.required<string>();

  readonly service = signal<ThreePLServiceDetails | undefined>(undefined);
  readonly loadFailed = signal(false);
  readonly derived = signal<InitialDerived | undefined>(undefined);
  readonly isShowMap = signal(false);
  readonly selectedTab = signal<ServiceTab>("info");

  private readonly translate = inject(TranslateService);
  private readonly split = inject<SplitService>("splitService" as never);
  readonly settings = this.split.getSitePackageDetailsSettings();
  readonly sitePrograms = this.split.getSiteProgramTypesMap();
  readonly serviceMetaData = this.split.getThirdPartyServicesMetadataSettings() ?? [];
  readonly validateTaskApprovalStatus = !!inject<SiteContextService>("siteContextService" as never)
    .getSiteInfo()?.validateTaskApprovalStatus;
  readonly updatePermissions = ["fulfillment.step.update", "fulfillment.service.update"];

  readonly isLargeScreen = toSignal(inject(BreakpointObserver).observe(MEDIA_GT_MD).pipe(map((s) => s.matches)), {initialValue: true});

  readonly tabs = computed<BzTab[]>(() => [
    {id: "info", label: this.translate.instant("Information")},
    {id: "timeline", label: this.translate.instant("Timeline")},
    ...(this.isLargeScreen() ? [] : [{id: "map", label: this.translate.instant("Map")}]),
  ]);

  readonly totalItems = computed(() => _.reduce(this.service()?.items, (sum, i) => sum + (i.count || 0), 0) || undefined);

  readonly icons = {
    car: bzIconPrivatecar,
    volume: bzIconVolume, weight: bzIconWeight, items: bzIconPackageOutline, hash: bzIconHash, created: bzIconEdit,
    label: bzIconLabelOutline, tag: bzIconTagOutline, vehicle: bzIconTruckOutline, program: bzIconProgram,
    account: bzIconAccount, carrier: bzIconCarrier, driver: bzIconAgent, phone: bzIconPhoneOutline, branch: bzIconBranch,
    pricing: bzIconDollarCircle, info: bzIconInfoOutline, map: bzIconMapOutline,
  };

  private readonly http = inject(ThreePLServiceHttpService);
  private readonly actions = inject(ThreePLServiceActionsService);
  private readonly channel = inject(ThreePLServicesChannelService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly commonsDialogService = inject<CommonsDialogService>("commonsDialogService" as never);
  private readonly actionReasonsDialogsService = inject<ActionReasonsDialogsServiceService>("actionReasonsDialogsService" as never);
  private readonly fulfillmentService = inject<FulfillmentService>("fulfillmentService" as never);
  private readonly fulfillmentDialogService = inject<FulfillmentDialogService>("fulfillmentDialogService" as never);
  private readonly log = inject<ILogService>("$log" as never);

  ngOnInit(): void {
    this.isShowMap.set(this.split.getShowMapInServiceView());
    this.http.getOne(this.serviceId()).subscribe({
      next: (service) => {
        this.derived.set(this.deriveOnce(service));
        this.service.set(service);
        this.listenForUpdates(service.id);
      },
      error: () => this.loadFailed.set(true),
    });
  }

  readonly branchIdsInput = computed(() => this.derived()?.branchIds as unknown as string[]);

  onTabChange(tabId: string): void {
    this.selectedTab.set(tabId as ServiceTab);
  }

  showMap(): void {
    this.isShowMap.set(true);
  }

  onAddNote({event, service}: ServiceActionEvent): void {
    Promise.resolve(this.commonsDialogService.note(event, [service.id]))
      .then((note) => this.actions.addNote([service.id], note))
      .catch(() => undefined);
  }

  onCancel({event, service}: ServiceActionEvent): void {
    Promise.resolve(this.actionReasonsDialogsService.createThreeplServiceCancelReason([service.id], event, "Cancel Service", true,
      [ActionReasonEntityEnum.Delivery, ActionReasonEntityEnum.Operation]))
      .then((payload) => this.actions.cancelService([service.id], payload.cancelReason))
      .catch(() => undefined);
  }

  onMoveToInternal({event, service}: ServiceActionEvent): void {
    const fulfillmentId = service.fulfillmentStepInfoList?.[0]?.fulfillmentId;
    if (!fulfillmentId) {
      return;
    }
    Promise.resolve(this.fulfillmentService.getFulfillment(fulfillmentId, true))
      .then((fulfillment) => this.fulfillmentDialogService.dispatchStep(fulfillment, undefined, event))
      .catch((error: unknown) => {
        if (error) {
          this.log.debug("Failed to dispatch step", error);
        } else {
          this.log.info("Dialog Closed");
        }
      });
  }

  private listenForUpdates(serviceId: string): void {
    this.channel.serviceUpdates(serviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({action, entity}) => this.service.update((current) => (current ? applyServicePush(current, action, entity) : current)));
  }

  private deriveOnce(service: ThreePLServiceDetails): InitialDerived {
    const steps = service.fulfillmentStepInfoList ?? [];
    return {
      omsOrderIds: _.uniq(steps.map((s) => s.omsOrderId).filter((id): id is string => !!id)),
      programNames: _.uniq(steps.map((s) => s.programType).filter((type): type is string => !!type).map((type) => this.sitePrograms[type])),
      branchIds: _.uniq(steps.map((s) => s.branchIds).filter((ids): ids is string[] => !!ids?.length)),
      dynamicColumnsMap: _.keyBy(service.metadata ?? [], "key"),
    };
  }
}
